<template>
    <div class="vehicle-info">
        <h1>{{ title }}</h1>

        <VehicleLocation
            :vehicle="vehicle"
            class="vehicle-info__location"
            :show-full-country-name="true"
            :show-icon="true"
        />

        <div class="vehicle-info__ratings-wrapper">
            <ul class="summary-bullets">
                <VehicleField type="value" :vehicle="vehicle" field="sleepsNumber" />
                <VehicleField type="value" :vehicle="vehicle" field="sitsNumber" />
            </ul>

            <Ratings
                :show-link="true"
                :value="vehicle.averageRating"
                :number-of-votes="vehicle.reviewCount"
                @link-clicked="$emit('link-clicked', $event)"
            />
        </div>

        <div class="vehicle-info__most-requested">
            <div class="vehicle-info__most-requested__item">
                <svg-icon name="license" />
                <span>{{ drivingLicenseEligibility }}</span>
            </div>
            <div v-if="vehicle.petsAllowed" class="vehicle-info__most-requested__item">
                <svg-icon name="pets" />
                <span>{{ $t('vehicle_info.allows_pets') }}</span>
            </div>
        </div>

        <div v-if="vehicleBoxInfoItems.length > 0" class="vehicle-info__boxes">
            <div v-for="(item, i) in vehicleBoxInfoItems" :key="i" class="vehicle-info__box-item">
                <svg-icon :name="item.iconName" />
                <div class="vehicle-info__box-item__content">
                    <div class="vehicle-info__box-item__title">{{ item.title }}</div>
                    <div class="vehicle-info__box-item__description">{{ item.description }}</div>
                </div>
            </div>
        </div>

        <VehicleOwnerShortInfo :user="vehicle.owner" @link-clicked="$emit('link-clicked', $event)" />
    </div>
</template>

<script>
import VehicleLocation from '~/components/vehicles/VehicleLocation';
import VehicleField from '~/components/vehicle/VehicleField';
import VehicleOwnerShortInfo from '~/components/vehicle/VehicleOwnerShortInfo';
import Ratings from '~/components/Ratings';
import { getFilterValue } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN } from '~/config/filterKeys';
import { INSTANT } from '~/config/bookingMethods';
import { ASSET_SUB_TYPE_CARAVAN } from '~/config/assetTypes';

export default {
    name: 'VehicleInfo',
    components: { Ratings, VehicleField, VehicleLocation, VehicleOwnerShortInfo },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        cancellationFreeUntil: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
    },
    computed: {
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        drivingLicenseEligibility() {
            if (this.vehicle.subType === ASSET_SUB_TYPE_CARAVAN) {
                return this.$t('vehicle_info.driving_license_caravan');
            }

            return this.vehicle.maximumAuthorizedMass <= 3500
                ? this.$t('vehicle_info.driving_license_b')
                : this.$t('vehicle_info.driving_license_c');
        },
        vehicleBoxInfoItems() {
            const items = [];

            if (this.vehicle.bookingMethod === INSTANT) {
                items.push({
                    iconName: 'instant',
                    title: this.$t(`pages.vehicle.instant_booking_label`),
                    description: this.$t(`pages.vehicle.instant_booking_description`),
                });
            }

            if (this.selectedDates?.from && this.cancellationFreeUntil) {
                items.push({
                    iconName: 'refund',
                    title: this.$t('pages.vehicle.free_cancellation_title'),
                    description: this.$t('pages.vehicle.free_cancellation_desc', {
                        date: this.cancellationFreeUntil,
                    }),
                });
            }

            return items;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.vehicle-info {
    display: flex;
    flex-direction: column;
    text-align: left;
    width: 100%;
    margin-bottom: 32px;
    @include font-size(16px);

    @include lg {
        margin-bottom: 40px;
    }

    .svg-icon {
        width: 24px;
        height: 24px;
        min-width: 24px;
    }

    h1 {
        @include font-size(24px);
        font-weight: 600;
        line-height: 30px;
        margin-bottom: 24px;

        @include lg {
            display: none;
        }
    }

    &__ratings-wrapper {
        @include lg {
            display: flex;
            align-items: center;
        }
    }

    .summary-bullets {
        display: flex;
        margin-bottom: 24px;
        font-weight: bold;

        @include lg {
            order: 1;
            margin: 0 0 0 32px;
        }
    }

    ::v-deep .ratings {
        @include font-size(16px);

        .svg-icon {
            width: 24px;
            height: 24px;

            @include lg {
                width: 32px;
                height: 32px;
            }
        }

        .ratings__value {
            margin: 0 4px;
        }

        @include lg {
            white-space: nowrap;
        }
    }

    &__location {
        margin-bottom: 24px;

        @include lg {
            display: none;
        }

        ::v-deep span {
            font-weight: normal;
        }
    }

    &__most-requested {
        @include font-size(14px);
        font-weight: 600;
        margin: 24px 0;

        .svg-icon {
            margin-right: 8px;
        }

        &__item {
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin-bottom: 12px;

                @include xl {
                    margin-right: 16px;
                    padding-right: 16px;
                    border-right: 1px solid $border-color;
                    margin-bottom: 0;
                }
            }
        }

        @include xl {
            display: flex;
            margin: 32px 0;
        }
    }

    &__boxes {
        @include lg {
            display: flex;
            gap: 8px;
            order: 1;
            margin-top: 32px;
        }
    }

    &__box-item {
        @extend %card;
        flex: 1;
        padding: 12px;
        margin-bottom: 8px;
        display: flex;
        align-items: center;

        .svg-icon {
            margin-right: 12px;
        }

        &__title {
            margin-bottom: 2px;
            font-weight: 600;
            @include font-size(14px);
        }

        &__description {
            @include font-size(13px);
        }

        @include lg {
            margin: 0;
            max-width: 50%;
        }
    }
}
</style>
